import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Save as SaveIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useContacts } from '../hooks/useContacts';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import ContactForm from '../components/ContactForm';
import { EMPTY_CONTACT } from '../utils/enums';
import { getCreatedId } from '../../../utils/helpers/getCreatedId';
import { FormPage, FormActions } from '../../../components/common/form';

export default function CreateContactPage() {
  const { t } = useTranslation(['contacts', 'common']);
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showSuccess, showError } = useSnackbar();
  const { createContact, loading } = useContacts();

  const [formData, setFormData] = useState({ ...EMPTY_CONTACT });
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }, [validationErrors]);

  const validate = useCallback(() => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = t('contacts:validation.name_required');
    if (!formData.contact_type) errors.contact_type = t('contacts:validation.type_required');
    return errors;
  }, [formData, t]);

  const buildPayload = useCallback(() => {
    return {
      name: formData.name.trim(),
      att: formData.att?.trim() || null,
      contact_type: formData.contact_type,
      role: formData.role?.trim() || null,
      phone_numbers: formData.phone_numbers
        .filter((p) => p.number?.trim())
        .map((p) => ({ country_code: p.country_code, number: p.number.trim(), label: p.label || null })),
      emails: formData.emails
        .filter((e) => e.email?.trim())
        .map((e) => ({ email: e.email.trim(), label: e.label || null })),
      address: formData.address && formData.address.address_line1?.trim() ? {
        address_line1: formData.address.address_line1.trim(),
        address_line2: formData.address.address_line2?.trim() || null,
        postal_code: formData.address.postal_code.trim(),
        city: formData.address.city.trim(),
        country: formData.address.country.trim(),
      } : null,
      website: formData.website?.trim() || null,
      cvr_number: formData.cvr_number?.trim() || null,
    };
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    const result = await createContact(buildPayload());
    if (result.success) {
      showSuccess(t('contacts:success.created'));
      // Open the new record (replace, so Back skips the filled-in form)
      const newId = getCreatedId(result, 'contact_id');
      navigate(newId ? `/contacts/${newId}` : '/contacts', { replace: true });
    } else {
      showError(result.error || t('contacts:errors.create_failed'));
    }
  }, [validate, createContact, buildPayload, showSuccess, showError, navigate, t]);

  return (
    <FormPage
      title={t('contacts:create_title')}
      onBack={() => navigateBack('/contacts')}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
    >
      <ContactForm
        formData={formData}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
        loading={loading}
      />
      <FormActions
        onCancel={() => navigateBack('/contacts')}
        submitType="submit"
        submitIcon={<SaveIcon />}
        loading={loading}
      />
    </FormPage>
  );
}
